import { useEffect, useRef } from 'react';

const PRODUCTS = [
  {
    id: 'vpn',
    name: 'VPN',
    tagline: 'Secure. Private. Always On.',
    description:
      'Our VPN application for iOS provides robust encryption and privacy protection for every connection you make. Designed with a clean, intuitive interface, it keeps your data secure whether you\'re on public Wi-Fi or your home network.',
    features: [
      'Military-grade encryption',
      'Zero-log policy',
      'One-tap connection',
      'Global server coverage',
    ],
    image:
      'https://public.readdy.ai/ai/img_res/ba3e4136bd5852cff4d12d53f05be2b5.jpg',
    accent: 'bg-brand-pale',
    pill: 'text-brand-DEFAULT bg-brand-pale',
    icon: 'ri-shield-keyhole-line',
  },
  {
    id: 'cleaner',
    name: 'Cleaner',
    tagline: 'Optimized. Fast. Efficient.',
    description:
      'The Cleaner app helps you reclaim storage space and keep your iOS device running smoothly. It intelligently identifies junk files, duplicate photos, and unused app data, giving your iPhone the performance boost it deserves.',
    features: [
      'Smart junk file detection',
      'Duplicate photo removal',
      'App cache cleanup',
      'Storage space analyzer',
    ],
    image:
      'https://readdy.ai/api/search-image?query=iPhone%20displaying%20a%20modern%20iOS%20cleaner%20app%20interface%20with%20circular%20progress%20indicators%20showing%20storage%20usage%20clean%20minimal%20design%20with%20soft%20green%20and%20white%20tones%20elegant%20typography%20optimization%20statistics%20visible%20on%20screen%20professional%20product%20photography%20white%20background%20precise%20detail&width=480&height=620&seq=cleaner-product-valdesta-1&orientation=portrait',
    accent: 'bg-gray-50',
    pill: 'text-gray-600 bg-gray-100',
    icon: 'ri-speed-up-line',
  },
];

export default function ProductsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    cardsRef.current.forEach((el) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(50px)';
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            cardsRef.current.forEach((el, i) => {
              if (!el) return;
              setTimeout(() => {
                el.style.transition = 'opacity 0.85s ease, transform 0.85s ease';
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 200);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" ref={sectionRef} className="py-32" style={{ background: '#F9FAF8' }}>
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <span className="w-8 h-px bg-brand-DEFAULT" />
          <span className="text-brand-DEFAULT text-xs font-medium tracking-[0.25em] uppercase">Our Products</span>
        </div>

        <div className="flex items-end justify-between mb-16">
          <h2 className="font-serif text-brand-dark" style={{ fontSize: 'clamp(32px, 3.5vw, 52px)', lineHeight: '1.15', fontWeight: 700 }}>
            iOS Applications<br />
            Built to <em className="not-italic text-brand-DEFAULT">Perform</em>
          </h2>
          <p className="text-gray-500 text-base max-w-sm text-right leading-relaxed">
            Each application is crafted specifically for iOS, taking full advantage
            of the platform's capabilities to deliver a native, premium experience.
          </p>
        </div>

        {/* Products */}
        <div className="flex flex-col gap-8">
          {PRODUCTS.map((product, index) => (
            <div
              key={product.id}
              ref={(el) => { cardsRef.current[index] = el; }}
              className={`rounded-2xl overflow-hidden ${product.accent}`}
            >
              <div className={`grid grid-cols-2 gap-0 ${index % 2 === 1 ? '' : ''}`}>
                {/* Image side */}
                <div
                  className={`relative flex items-end justify-center pt-12 px-16 ${index % 2 === 1 ? 'order-2' : 'order-1'}`}
                  style={{ background: index % 2 === 0 ? '#EAF2EE' : '#EDEEEC', minHeight: '420px' }}
                >
                  <div className="w-48 h-80 rounded-3xl overflow-hidden" style={{ marginBottom: '-1px' }}>
                    <img
                      src={product.image}
                      alt={`${product.name} iOS app`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Decorative pill */}
                  <div className="absolute top-8 left-8">
                    <span className={`inline-flex items-center gap-2 rounded-full text-xs font-medium px-4 py-2 ${product.pill}`}>
                      <i className={`${product.icon} text-sm`} />
                      iOS Application
                    </span>
                  </div>
                </div>

                {/* Content side */}
                <div
                  className={`flex flex-col justify-center px-14 py-16 ${index % 2 === 1 ? 'order-1' : 'order-2'}`}
                >
                  <p className="text-brand-DEFAULT text-xs font-medium tracking-[0.2em] uppercase mb-4">{product.tagline}</p>
                  <h3 className="font-serif text-brand-dark mb-5" style={{ fontSize: '42px', fontWeight: 700, lineHeight: 1.1 }}>
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-base leading-relaxed mb-10">{product.description}</p>

                  <ul className="grid grid-cols-2 gap-3">
                    {product.features.map((feat) => (
                      <li key={feat} className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-pale text-brand-DEFAULT flex-shrink-0">
                          <i className="ri-check-line text-xs" />
                        </span>
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
